import * as _ from 'lodash';
import * as hash from 'object-hash';
import { Component, FormEvent } from 'react';
import { connect, Dispatch } from 'react-redux';
import { AppActionCreators, CenterActionCreators, IApplicationState, ICenterSerologyDataResponse } from '@nkr/store/index';
import { ICenterAddress, ISerologyData, ISerologyTest, IUpdateSerologyDataRequest } from '@nkr/models/index';
import { OrganType } from '@nkr/enums/index';
import { ISerologyDataValidation } from './validation';
import { Checkbox, Col, ControlLabel, FormControl, FormGroup, HelpBlock, Label, Radio, Row } from 'react-bootstrap';
import { FormWrapper } from '../styles';
import { IErrorItem } from '../../error-item';
import { initialSerologyData, initialSerologyValidationData } from '../initial-data';
import CenterProfileNav from '../edit-nav';
import { profileNavKey } from '../../profile-nav-key';
import { Validate } from '../../validate';
import { toast } from 'react-toastify';

interface IProps {
    centerId: number,
    organType: OrganType,
    isAdmin: boolean,
    isAdminUser: boolean,
    hasDataChanges: boolean,
    serologyTestList: ISerologyTest[],
    centerAddressList: ICenterAddress[],
    onTabClick: (href: string) => void,
    onDataChanged: (hasDataChanged: boolean) => void,
    updateErrors: (list: IErrorItem[]) => void
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getData: (centerId: number, organType: OrganType) => Promise<ICenterSerologyDataResponse>,
    updateData: (data: IUpdateSerologyDataRequest) => Promise<ICenterSerologyDataResponse>,
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    data: ISerologyData,
    validation: ISerologyDataValidation,
    hash: string,
    isDirty: boolean,
    isValid: boolean
}

class CenterSerologyTab extends Component<TProps, IState> {
    readonly state: IState = {
        data: initialSerologyData,
        validation: initialSerologyValidationData,
        hash: '',
        isDirty: false,
        isValid: true
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting serology/tests data, one moment please...').then(() => {
            this.props.getData(this.props.centerId, this.props.organType).then(response => {
                this.setState({
                    data: response.data.results,
                    hash: hash(response.data.results)
                });

                this.props.hideLoading();
            });
        });
    }

    save = () => {
        let { serologyMaxDaysBeforeSurgery, requiresQfg, serologyShipToId, serologyTests } = this.state.data;

        let data: IUpdateSerologyDataRequest = {
            id: this.props.centerId,
            organType: this.props.organType,
            serologyMaxDaysBeforeSurgery,
            requiresQfg,
            serologyShipToId,
            serologyTests
        };

        this.props.showLoading('Saving serology/test data, one moment please...').then(() => {
            this.props.updateData(data).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    this.setState({
                        data: response.data.results,
                        hash: hash(response.data.results)
                    }, () => {
                        toast.success('Serology/Test data updated successfully');

                        this.props.onDataChanged(false);
                    });
                }
            });
        });
    }

    validateBeforeSave = () => {
        let validation = Validate.validateSerologyData(this.state.data, this.props.organType);
        let isValid = Validate.isSerologyDataValid(validation);
        let errorList = Validate.getInvalidSerologyDataItems(validation).filter(x => x.show);

        this.setState({
            validation,
            isValid,
            isDirty: true,
        }, () => {
            this.props.updateErrors(errorList);

            if (this.state.isValid) {
                this.save();
            } else {
                window.scrollTo(0, 0);
            }
        });
    }

    handleFieldChange = (e: FormEvent<FormControl & HTMLSelectElement & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(this.state.data);

        if (name == 'serologyMaxDaysBeforeSurgery') {
            data.serologyMaxDaysBeforeSurgery = parseInt(value);
        }

        if (name == 'serologyShipToId') {
            data.serologyShipToId = parseInt(value);
        }

        if (name == 'requiresQfg') {
            data.requiresQfg = parseInt(value);
        }

        let newHash = hash(data);

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(newHash != this.state.hash);
        });
    }

    handleSerologyTestChange = (id: number) => {
        let data = _.cloneDeep(this.state.data);

        if (data.serologyTests.indexOf(id) >= 0) {
            data.serologyTests = data.serologyTests.filter(x => x != id);
        } else {
            data.serologyTests.push(id);
        }

        let newHash = hash(data);

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(newHash != this.state.hash);
        });
    }

    render = () => {
        const daysBeforeSurgeryList = _.range(10, 22);

        const { validation } = this.state;
        const { requiresQfg, serologyMaxDaysBeforeSurgery, serologyShipToId } = this.state.data;

        const sortedSerologyTests = _.sortBy(this.props.serologyTestList, x => x.name);

        return (
            <>
                <CenterProfileNav
                    activeKey={profileNavKey.SEROLOGY_TESTS}
                    isAdmin={this.props.isAdmin}
                    isAdminUser={this.props.isAdminUser}
                    centerId={this.props.centerId}
                    hasChanges={this.props.hasDataChanges}
                    onTabClick={this.props.onTabClick}
                    onSave={this.validateBeforeSave}
                />

                <div style={{ padding: 10, borderWidth: '1px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ddd', borderRadius: 4 }}>
                    <div style={{ padding: 20 }}>
                        <FormWrapper horizontal style={{ marginBottom: 20 }}>
                            {/*
                            <FormGroup validationState={this.state.isDirty && this.state.validation.serologyMaxDaysBeforeSurgery == false ? 'error' : null}>
                                <Col xs={12} sm={4} componentClass={ControlLabel} style={{ paddingTop: 0 }}>
                                    Select the # of calendar days before your patient's transplant date that pre-op serology test results are needed for recipients at your center.
                                </Col>
                                <Col xs={12} sm={5} style={{ paddingTop: 0 }}>
                                    <FormControl
                                        componentClass='select'
                                        name='serologyMaxDaysBeforeSurgery'
                                        value={serologyMaxDaysBeforeSurgery}
                                        onChange={this.handleFieldChange}
                                    >
                                        <option value={-1}>- select -</option>
                                        {daysBeforeSurgeryList.map(item =>
                                            <option value={item}>{item}</option>
                                        )}
                                    </FormControl>
                                    <HelpBlock>
                                        (Based on the date you have selected and in order to accommodate a 3 day turnaround, serology tests will be requested
                                        <span style={{ fontWeight: 700 }}> {this.state.data.serologyMaxDaysBeforeSurgery + 5} calendar days</span> prior to the donor surgery date.)
                                    </HelpBlock>
                                </Col>
                            </FormGroup>
                            */}

                            {this.props.organType == OrganType.Kidney && (
                                <FormGroup validationState={this.state.isDirty && !validation.serologyShipToId ? 'error' : null}>
                                    <Col xs={12} sm={4} componentClass={ControlLabel}>Which address should serology blood kits ship to?</Col>
                                    <Col xs={12} sm={5}>
                                        <FormControl
                                            componentClass='select'
                                            name='serologyShipToId'
                                            value={serologyShipToId}
                                            onChange={this.handleFieldChange}
                                        >
                                            <option value={-1}>- select -</option>
                                            {this.props.centerAddressList.map(item =>
                                                <option value={item.id}>{item.typeName}</option>
                                            )}
                                        </FormControl>
                                    </Col>
                                </FormGroup>
                            )}

                            {this.props.organType == OrganType.Kidney && (
                                <FormGroup>
                                    <Col xs={12} lg={4} componentClass={ControlLabel}>Does your center require a QuantiFERON-TB Gold test for all Donors</Col>
                                    <Col xs={12} lg={5}>
                                        <Radio id='requiresQfgYes' name='requiresQfg' inline checked={requiresQfg == 1} value={1} onChange={this.handleFieldChange}>Yes</Radio>
                                        <Radio id='requiresQfgNo' name='requiresQfg' inline checked={requiresQfg == 0} value={0} onChange={this.handleFieldChange}>No</Radio>
                                        <Radio id='requiresQfgUnk' name='requiresQfg' inline checked={requiresQfg == -1} value={-1} onChange={this.handleFieldChange}>Unknown</Radio>
                                    </Col>
                                </FormGroup>
                            )}

                            <FormGroup>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>Select from the following list the UNOS mandated pre-op serology tests that your center requires from a donor center:</Col>
                                <Col xs={12} sm={6}>
                                    <div style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: 4 }}>
                                        <Row>
                                            {sortedSerologyTests.map(item => {
                                                let isSelected = this.state.data.serologyTests.indexOf(item.id) >= 0;

                                                let label = <>{item.name}</>;

                                                if (item.isRequired && this.props.isAdminUser) {
                                                    label = <span style={{ backgroundColor: 'yellow' }}>{item.name}</span>;
                                                }

                                                return (
                                                    <Col xs={12} sm={6}>
                                                        <Checkbox style={{ display: 'inline-block', marginRight: 10 }} checked={isSelected} disabled={item.isRequired && !this.props.isAdminUser} value={item.id} onClick={() => this.handleSerologyTestChange(item.id)}>{label}</Checkbox>
                                                        {item.isRequired == 1 && (
                                                            <span style={{ color: '#C00', fontSize: 12, fontStyle: 'italic' }}>* required by OPTN </span>
                                                        )}
                                                        {item.isRequired == 2 && (
                                                            <span style={{ color: '#C00', fontSize: 12, fontStyle: 'italic' }}>* required</span>
                                                        )}
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    </div>
                                    {this.props.isAdminUser && <span style={{ backgroundColor: 'yellow', width: 'initial' }}>* Only admin can change these tests...</span>}
                                </Col>
                            </FormGroup>
                        </FormWrapper>
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {}
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getData: (centerId, organType) => {
            return dispatch(CenterActionCreators.getSerologyData(centerId, organType));
        },
        updateData: (data) => {
            return dispatch(CenterActionCreators.updateSerologyData(data));
        },
    }
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterSerologyTab);